import { useSceneEvents } from "@/components/common/scene-events-context";
import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { MiniMapOverlay } from "@/components/r3f/utils/minimap/minimap-overlay";
import { SceneContextMenu } from "@/components/ui/scene-context-menu";
import { useCurrentScene } from "@/modes/mode-data-context";
import {
  useOverlayElements,
  useOverlayRef,
} from "@/modes/overlay-elements-context";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import { setShouldUseIntensityData } from "@/store/modes/walk-mode-slice";
import { selectActiveElement } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import { selectPanoAnnotationsAdjustedPose } from "@/utils/camera-transform";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { parseVector3, selectIElement } from "@faro-lotv/app-component-toolbox";
import {
  GUID,
  IElementImg360,
  isIElementImg360,
  isIElementOdometryPathSection,
} from "@faro-lotv/ielement-types";
import { Stack } from "@mui/material";
import { useCallback, type JSX } from "react";
import { Matrix4 } from "three";
import { matrix4ToAlignmentTransform } from "../alignment-modes-commons/alignment-transform";
import {
  WalkOverlay,
  WalkOverlayBottomButtons,
} from "../walk-mode/walk-overlay";
import { selectBestImage } from "../walk-mode/walk-scene-selectors";
import { isFlyWalkImageElement } from "./flywalk-types";

/** @returns The overlay in Walk Mode */
export function FlyWalkOverlay(): JSX.Element | null {
  const currentScene = useCurrentScene();
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const activeTool = useAppSelector(selectActiveTool);

  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);

  const camera = useActiveCamera().activeCamera;
  const onSceneFilterChanged = useCallback(
    (type: SceneFilter) => {
      dispatch(setFlyWalkSceneFilter(type));
      if (type === SceneFilter.Pano) {
        // If the user sets the 'pano' scene filter,
        // the active pano is computed as the closest pano to the
        // current camera location
        const state = getState();
        const activePano = selectBestImage(
          currentScene,
          camera.position,
          true,
        )(state);
        if (activePano) {
          dispatch(setActiveElement(activePano.id));
        }
      }
    },
    [dispatch, currentScene, getState, camera],
  );

  const onActiveElementChanged = useCallback(
    (id: GUID) => {
      const state = getState();
      const nextActiveElement = selectIElement(id)(state);
      if (
        sceneFilter === SceneFilter.Pano &&
        nextActiveElement &&
        !isFlyWalkImageElement(nextActiveElement)
      ) {
        // Set a smarter active element only once, instead of setting an active element that must be re-set
        // by the mode dispatch logic
        const activeImg = selectBestImage(
          currentScene,
          camera.position,
          false,
          nextActiveElement,
        )(state);
        dispatch(setActiveElement(activeImg?.id ?? id));
      } else {
        dispatch(setActiveElement(id));
      }
    },
    [dispatch, getState, sceneFilter, camera, currentScene],
  );

  const onPanoTypeChanged = useCallback(
    (showIntensity: boolean, siblingPano: IElementImg360) => {
      dispatch(setShouldUseIntensityData(showIntensity));
      dispatch(setActiveElement(siblingPano.id));
    },
    [dispatch],
  );

  const { setMiniMap, setMiniMapCanvas } = useOverlayElements();
  const miniMapRef = useOverlayRef<HTMLDivElement>((r) => {
    setMiniMap(r);
  });
  const miniMapCanvasRef = useOverlayRef<HTMLCanvasElement>(setMiniMapCanvas);

  const activeElement =
    sceneFilter === SceneFilter.Cad ? currentScene.cad : currentScene.main;

  const forceMinimizingMinimap = activeTool === ToolName.annotation;

  // Use the name of the first visible sheet for minimap's title.
  const singleSheetForMinimapTitle = currentScene.activeSheets.at(0);

  const { lookAt } = useSceneEvents();
  const onPhotoObjectChanged = useCallback(
    (id: GUID) => {
      const state = getState();
      const activePano = selectActiveElement(state);
      if (!!activePano && isIElementImg360(activePano)) {
        const panoAdjustedPose = new Matrix4().fromArray(
          selectPanoAnnotationsAdjustedPose(activePano)(state),
        );
        const imageCommandWorldTransform =
          selectIElementWorldMatrix4(id)(state);
        const { position } = matrix4ToAlignmentTransform(
          panoAdjustedPose.clone().multiply(imageCommandWorldTransform),
        );
        lookAt.emit(parseVector3(position).clone());
      }
    },
    [lookAt, getState],
  );

  return (
    <>
      <Stack
        direction="column"
        sx={{ justifyContent: "space-between", height: "100%", width: "100%" }}
      >
        {activeElement && (
          <WalkOverlay
            sceneFilter={sceneFilter}
            cad={currentScene.cad}
            hasPanosOrPhotos={
              !!currentScene.panos.length ||
              !!currentScene.paths.filter(isIElementOdometryPathSection)
                .length ||
              !!currentScene.photos.length ||
              !!currentScene.orthomosaics.length
            }
            activeWalkElement={activeElement}
            referenceElement={currentScene.referenceElement}
            isMeasuring={activeTool === ToolName.measurement}
            onSceneFilterChanged={onSceneFilterChanged}
            onActiveElementChanged={onActiveElementChanged}
            onPhotoObjectChanged={onPhotoObjectChanged}
            onPanoTypeChanged={onPanoTypeChanged}
          />
        )}
        <Stack direction="row" sx={{ justifyContent: "center" }}>
          <WalkOverlayBottomButtons
            activeElement={currentScene.main}
            walkSceneFilter={sceneFilter}
          />
        </Stack>
        <SceneContextMenu />
      </Stack>

      <MiniMapOverlay
        eventDivRef={miniMapRef}
        canvasRef={miniMapCanvasRef}
        activeSheetName={singleSheetForMinimapTitle?.name}
        forceMinimized={forceMinimizingMinimap}
      />
    </>
  );
}
